import { useEffect, RefObject } from 'react';

type AnyRef = RefObject<HTMLElement | null | undefined> | HTMLElement | null | undefined;

interface UseClickOutsideOptions {
  enabled?: boolean;
  onEscape?: () => void;
  ignoreEscape?: boolean;
  eventTypes?: Array<'pointerdown' | 'mousedown' | 'touchstart' | 'click'>;
}

/**
 * Standardized Global Outside-Click & Escape Dismissal Hook (V5.15-R7)
 *
 * Guarantees:
 * 1. Clicking ANYWHERE outside the referenced element(s) immediately closes the popover/dropdown.
 * 2. Does NOT intercept or block clicks to other buttons/dropdowns (allows single-click cross-interaction).
 * 3. Clicking inside the referenced element(s) does not trigger dismissal.
 * 4. Pressing Escape closes the open popover/dropdown.
 * 5. Touch/mobile tap outside works seamlessly.
 */
export function useClickOutside(
  targetRefs: AnyRef | AnyRef[],
  isOpen: boolean,
  onClose: () => void,
  options: UseClickOutsideOptions = {}
) {
  const {
    enabled = true,
    onEscape,
    ignoreEscape = false,
    eventTypes = ['pointerdown', 'touchstart']
  } = options;

  useEffect(() => {
    if (!isOpen || !enabled) return;

    const refs = Array.isArray(targetRefs) ? targetRefs : [targetRefs];

    const isInside = (target: Node | null): boolean => {
      if (!target) return false;
      return refs.some((r) => {
        if (!r) return false;
        const el = 'current' in r ? r.current : r;
        return el ? el.contains(target) : false;
      });
    };

    const handlePointerOrTouch = (event: Event) => {
      const target = event.target as Node | null;
      if (!isInside(target)) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!ignoreEscape && event.key === 'Escape') {
        if (onEscape) {
          onEscape();
        } else {
          onClose();
        }
      }
    };

    // Use passive listener for touch/pointer performance
    eventTypes.forEach((evt) => {
      document.addEventListener(evt, handlePointerOrTouch, { passive: true });
    });

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      eventTypes.forEach((evt) => {
        document.removeEventListener(evt, handlePointerOrTouch);
      });
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, enabled, onClose, onEscape, ignoreEscape, ...((Array.isArray(targetRefs) ? targetRefs : [targetRefs]).map(r => r && 'current' in r ? r.current : r))]);
}
